import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Gift, 
  Vote,
  Users,
  Clock,
  LogOut,
  User,
  Settings,
  Trophy,
  Sparkles,
  ChevronRight,
  Loader2,
  Bell,
  BellOff,
  Camera,
  Upload,
  X,
  Trash2,
  Package,
  MapPin,
  Phone,
  Calendar,
  CheckCircle2,
  Menu,
  ChevronDown
} from "lucide-react";
import { toast } from "sonner";
import AnimatedLogo, { AnimatedFooter } from "../components/AnimatedLogo";
import MemberMenu from "../components/MemberMenu";
import InstallPWAButton from "../components/InstallPWAButton";
import NotificationCenter, { useNotifications } from "../components/NotificationCenter";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import api from "../lib/api";
import { formatDate, getImageUrl } from "../lib/utils";

export default function PlayPortal() {
  const navigate = useNavigate();
  const [member, setMember] = useState(null);
  const [draws, setDraws] = useState([]);
  const [polls, setPolls] = useState([]);
  const [participations, setParticipations] = useState({ draws: [], polls: [] });
  const [wonPrizes, setWonPrizes] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [activeTab, setActiveTab] = useState("discover");
  const fileInputRef = useRef(null);

  // Notifications
  const memberId = member?.id || "guest";
  const { notifications, addNotification, markAsRead, clearAll } = useNotifications(memberId);

  useEffect(() => {
    const token = localStorage.getItem("tireo_member_token");
    const memberData = localStorage.getItem("tireo_member");
    
    if (!token || !memberData) {
      navigate("/join");
      return;
    }
    
    setMember(JSON.parse(memberData));
    loadData(token);
  }, []);

  const loadData = async (token) => {
    try {
      // Set auth header
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      
      const [drawsRes, pollsRes, participationsRes, profileRes, subsRes, prizesRes] = await Promise.all([
        api.get("/public/discover/draws"),
        api.get("/public/discover/polls"),
        api.get("/member-portal/participations"),
        api.get("/member-portal/me"),
        api.get("/member-portal/subscriptions").catch(() => ({ data: [] })),
        api.get("/member-portal/won-prizes").catch(() => ({ data: [] }))
      ]);
      
      setDraws(drawsRes.data);
      setPolls(pollsRes.data);
      setParticipations(participationsRes.data);
      setMember(profileRes.data);
      setSubscriptions(profileRes.data.organizer_subscriptions || []);
      setWonPrizes(prizesRes.data || []);
      localStorage.setItem("tireo_member", JSON.stringify(profileRes.data));
    } catch (error) {
      console.error("Error loading data:", error);
      if (error.response?.status === 401) {
        localStorage.removeItem("tireo_member_token");
        localStorage.removeItem("tireo_member");
        navigate("/join");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleParticipate = async (drawId) => {
    const token = localStorage.getItem("tireo_member_token");
    if (!token) {
      navigate("/join");
      return;
    }
    
    try {
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      const res = await api.post(`/public/draw/${drawId}/participate`);
      toast.success(`Participation enregistrée ! Ref: ${res.data.reference}`);
      loadData(token);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la participation");
    }
  };

  const isParticipating = (drawId) => {
    return participations.draws?.some(d => d.id === drawId);
  };

  // Upload avatar
  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith("image/")) {
      toast.error("Veuillez sélectionner une image");
      return;
    }
    
    setUploadingAvatar(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const uploadRes = await api.post("/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      // Update profile with new avatar
      await api.put("/member-portal/me", { avatar_url: uploadRes.data.url });
      
      setMember(prev => ({ ...prev, avatar_url: uploadRes.data.url }));
      localStorage.setItem("tireo_member", JSON.stringify({ ...member, avatar_url: uploadRes.data.url }));
      toast.success("Photo mise à jour");
    } catch (error) {
      toast.error("Erreur lors du téléchargement");
    } finally {
      setUploadingAvatar(false);
    }
  };

  // Unsubscribe from organizer
  const handleUnsubscribe = async (organizerId) => {
    try {
      await api.delete(`/member-portal/subscriptions/${organizerId}`);
      setSubscriptions(prev => prev.filter(s => s.organizer_id !== organizerId));
      setMember(prev => ({
        ...prev,
        organizer_subscriptions: prev.organizer_subscriptions?.filter(s => s.organizer_id !== organizerId) || []
      }));
      toast.success("Désabonnement réussi");
    } catch (error) {
      toast.error("Erreur lors du désabonnement");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("tireo_member_token");
    localStorage.removeItem("tireo_member");
    navigate("/");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-500 to-orange-500">
        <Loader2 className="w-12 h-12 text-white animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      {/* Hidden file input for avatar */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleAvatarUpload}
        className="hidden"
      />

      {/* Header */}
      <header className="bg-gradient-to-r from-amber-500 to-orange-500 text-white sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <AnimatedLogo linkTo="/" showAnimation={true} size="default" variant="light" />
          
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Install App Button */}
            <InstallPWAButton />
            
            {/* Mobile Navigation Menu */}
            <div className="sm:hidden">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="text-white hover:bg-white/20 gap-1 px-2">
                    <Menu className="w-5 h-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem onClick={() => setActiveTab("draws")} className="gap-3">
                    <Gift className="w-4 h-4 text-amber-500" />
                    <span>Tirages au sort</span>
                    {activeTab === "draws" && <CheckCircle2 className="w-4 h-4 text-green-500 ml-auto" />}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setActiveTab("polls")} className="gap-3">
                    <Vote className="w-4 h-4 text-purple-500" />
                    <span>Sondages</span>
                    {activeTab === "polls" && <CheckCircle2 className="w-4 h-4 text-green-500 ml-auto" />}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setActiveTab("prizes")} className="gap-3">
                    <Package className="w-4 h-4 text-yellow-500" />
                    <span>Mes lots ({wonPrizes.filter(p => !p.is_withdrawn).length})</span>
                    {activeTab === "prizes" && <CheckCircle2 className="w-4 h-4 text-green-500 ml-auto" />}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setActiveTab("subscriptions")} className="gap-3">
                    <Bell className="w-4 h-4 text-blue-500" />
                    <span>Abonnements ({subscriptions.filter(s => s.is_active).length})</span>
                    {activeTab === "subscriptions" && <CheckCircle2 className="w-4 h-4 text-green-500 ml-auto" />}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setActiveTab("history")} className="gap-3">
                    <Clock className="w-4 h-4 text-slate-500" />
                    <span>Participations</span>
                    {activeTab === "history" && <CheckCircle2 className="w-4 h-4 text-green-500 ml-auto" />}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            
            {/* Notifications */}
            <NotificationCenter 
              notifications={notifications}
              onMarkAsRead={markAsRead}
              onClear={clearAll}
            />
            <span className="text-white/90 text-sm hidden sm:block">{member?.name}</span>
            <MemberMenu 
              member={member} 
              onLogout={handleLogout}
              onUpdateAvatar={() => fileInputRef.current?.click()}
            />
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Welcome Banner */}
        <div className="bg-gradient-to-r from-amber-100 to-orange-100 rounded-2xl p-4 sm:p-6 mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-slate-800 mb-1" style={{ fontFamily: 'Outfit' }}>
                Bienvenue, {member?.name?.split(' ')[0]} ! 🎉
              </h1>
              <p className="text-sm sm:text-base text-slate-600">
                Découvrez les jeux et tirages au sort disponibles
              </p>
            </div>
            <div className="flex items-center gap-2">
              {member?.accept_marketing ? (
                <Badge className="bg-green-100 text-green-700 flex items-center gap-1 text-xs">
                  <Bell className="w-3 h-3" />
                  Notifications activées
                </Badge>
              ) : (
                <Badge className="bg-slate-100 text-slate-600 flex items-center gap-1 text-xs">
                  <BellOff className="w-3 h-3" />
                  Notifications désactivées
                </Badge>
              )}
            </div>
          </div>
        </div>

        {/* Stats - Clickable cards that navigate to tabs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <Card 
            className="cursor-pointer hover:border-amber-400 hover:shadow-md transition-all active:scale-95"
            onClick={() => setActiveTab("draws")}
          >
            <CardContent className="p-4 flex items-center gap-3">
              <Gift className="w-8 h-8 text-amber-500" />
              <div>
                <p className="text-2xl font-bold">{draws.length}</p>
                <p className="text-sm text-muted-foreground">Tirages dispo</p>
              </div>
            </CardContent>
          </Card>
          <Card 
            className="cursor-pointer hover:border-purple-400 hover:shadow-md transition-all active:scale-95"
            onClick={() => setActiveTab("polls")}
          >
            <CardContent className="p-4 flex items-center gap-3">
              <Vote className="w-8 h-8 text-purple-500" />
              <div>
                <p className="text-2xl font-bold">{polls.length}</p>
                <p className="text-sm text-muted-foreground">Sondages dispo</p>
              </div>
            </CardContent>
          </Card>
          <Card 
            className="cursor-pointer hover:border-yellow-400 hover:shadow-md transition-all active:scale-95"
            onClick={() => setActiveTab("prizes")}
          >
            <CardContent className="p-4 flex items-center gap-3">
              <Trophy className="w-8 h-8 text-yellow-500" />
              <div>
                <p className="text-2xl font-bold">{wonPrizes.filter(p => !p.is_withdrawn).length}</p>
                <p className="text-sm text-muted-foreground">Lots gagnés</p>
              </div>
            </CardContent>
          </Card>
          <Card 
            className="cursor-pointer hover:border-blue-400 hover:shadow-md transition-all active:scale-95"
            onClick={() => setActiveTab("subscriptions")}
          >
            <CardContent className="p-4 flex items-center gap-3">
              <Users className="w-8 h-8 text-blue-500" />
              <div>
                <p className="text-2xl font-bold">{member?.organizer_subscriptions?.filter(s => s.is_active)?.length || 0}</p>
                <p className="text-sm text-muted-foreground">Abonnements</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="draws" value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          {/* Tab list hidden on mobile (use header menu instead) */}
          <TabsList className="bg-card border shadow-sm w-full justify-start hidden sm:flex">
            <TabsTrigger value="draws" className="data-[state=active]:bg-amber-100 data-[state=active]:text-amber-700 text-sm whitespace-nowrap">
              <Gift className="w-4 h-4 mr-2" />
              Tirages au sort
            </TabsTrigger>
            <TabsTrigger value="polls" className="data-[state=active]:bg-purple-100 data-[state=active]:text-purple-700 text-sm whitespace-nowrap">
              <Vote className="w-4 h-4 mr-2" />
              Sondages
            </TabsTrigger>
            <TabsTrigger value="prizes" className="data-[state=active]:bg-yellow-100 data-[state=active]:text-yellow-700 text-sm whitespace-nowrap">
              <Package className="w-4 h-4 mr-2" />
              Mes lots ({wonPrizes.filter(p => !p.is_withdrawn).length})
            </TabsTrigger>
            <TabsTrigger value="subscriptions" className="data-[state=active]:bg-blue-100 data-[state=active]:text-blue-700 text-sm whitespace-nowrap">
              <Bell className="w-4 h-4 mr-2" />
              Abonnements ({subscriptions.filter(s => s.is_active).length})
            </TabsTrigger>
            <TabsTrigger value="history" className="data-[state=active]:bg-slate-100 data-[state=active]:text-slate-700 text-sm whitespace-nowrap">
              <Clock className="w-4 h-4 mr-2" />
              Participations
            </TabsTrigger>
          </TabsList>

          {/* Mobile: Current tab indicator */}
          <div className="sm:hidden flex items-center justify-between bg-card border rounded-lg p-3 shadow-sm">
            <div className="flex items-center gap-2">
              {activeTab === "draws" && <Gift className="w-5 h-5 text-amber-500" />}
              {activeTab === "polls" && <Vote className="w-5 h-5 text-purple-500" />}
              {activeTab === "prizes" && <Package className="w-5 h-5 text-yellow-500" />}
              {activeTab === "subscriptions" && <Bell className="w-5 h-5 text-blue-500" />}
              {activeTab === "history" && <Clock className="w-5 h-5 text-slate-500" />}
              <span className="font-medium">
                {activeTab === "draws" && "Tirages au sort"}
                {activeTab === "polls" && "Sondages"}
                {activeTab === "prizes" && `Mes lots (${wonPrizes.filter(p => !p.is_withdrawn).length})`}
                {activeTab === "subscriptions" && `Abonnements (${subscriptions.filter(s => s.is_active).length})`}
                {activeTab === "history" && "Participations"}
              </span>
            </div>
            <span className="text-xs text-muted-foreground">
              Utilisez le menu ☰ pour naviguer
            </span>
          </div>

          {/* Draws Tab */}
          <TabsContent value="draws">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {draws.length === 0 ? (
                <div className="col-span-full text-center py-12">
                  <Gift className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">Aucun tirage disponible pour le moment</p>
                </div>
              ) : (
                draws.map(draw => (
                  <Card key={draw.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                    <div className="h-32 bg-gradient-to-br from-amber-400 to-orange-500 relative">
                      {draw.organizer_logo && (
                        <img 
                          src={draw.organizer_logo} 
                          alt={draw.organizer_name}
                          className="absolute inset-0 w-full h-full object-contain p-4"
                        />
                      )}
                      {isParticipating(draw.id) && (
                        <Badge className="absolute top-2 right-2 bg-green-500">
                          Inscrit
                        </Badge>
                      )}
                    </div>
                    <CardContent className="p-4">
                      <h3 className="font-bold text-lg mb-1">{draw.title}</h3>
                      <p className="text-sm text-muted-foreground mb-3">
                        Par {draw.organizer_name}
                      </p>
                      
                      <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                        <span className="flex items-center gap-1">
                          <Gift className="w-4 h-4" />
                          {draw.prizes?.length || 0} lots
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          {draw.participant_counter || 0} inscrits
                        </span>
                      </div>
                      
                      {isParticipating(draw.id) ? (
                        <Link to={`/draw/${draw.id}/results`}>
                          <Button variant="outline" className="w-full rounded-full">
                            Voir les résultats
                            <ChevronRight className="w-4 h-4 ml-2" />
                          </Button>
                        </Link>
                      ) : (
                        <Button 
                          className="w-full rounded-full bg-gradient-to-r from-amber-500 to-orange-500"
                          onClick={() => handleParticipate(draw.id)}
                        >
                          <Sparkles className="w-4 h-4 mr-2" />
                          Participer
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Polls Tab */}
          <TabsContent value="polls">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {polls.length === 0 ? (
                <div className="col-span-full text-center py-12">
                  <Vote className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">Aucun sondage disponible pour le moment</p>
                </div>
              ) : (
                polls.map(poll => (
                  <Card key={poll.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                    <div className="h-32 bg-gradient-to-br from-purple-400 to-pink-500 relative flex items-center justify-center">
                      <Vote className="w-12 h-12 text-white/80" />
                    </div>
                    <CardContent className="p-4">
                      <h3 className="font-bold text-lg mb-1">{poll.title}</h3>
                      <p className="text-sm text-muted-foreground mb-3">
                        Par {poll.organizer_name}
                      </p>
                      
                      <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                        <span className="flex items-center gap-1">
                          <Vote className="w-4 h-4" />
                          {poll.options?.length || 0} options
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          {poll.total_votes || 0} votes
                        </span>
                      </div>
                      
                      <Link to={`/poll/${poll.id}/vote`}>
                        <Button className="w-full rounded-full bg-gradient-to-r from-purple-500 to-pink-500">
                          Voter
                          <ChevronRight className="w-4 h-4 ml-2" />
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Prizes Tab - Mes Lots */}
          <TabsContent value="prizes">
            <div className="space-y-4">
              {wonPrizes.length === 0 ? (
                <Card>
                  <CardContent className="p-8 text-center">
                    <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <h3 className="font-bold text-lg mb-2">Aucun lot gagné</h3>
                    <p className="text-muted-foreground">
                      Participez aux tirages au sort pour tenter de gagner des lots !
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <>
                  {/* Lots à retirer */}
                  {wonPrizes.filter(p => !p.is_withdrawn).length > 0 && (
                    <div className="space-y-3">
                      <h3 className="font-bold text-lg flex items-center gap-2 text-yellow-600">
                        <Package className="w-5 h-5" />
                        Lots à retirer ({wonPrizes.filter(p => !p.is_withdrawn).length})
                      </h3>
                      {wonPrizes.filter(p => !p.is_withdrawn).map(prize => (
                        <Card key={prize.id} className="overflow-hidden border-yellow-200 bg-yellow-50/50">
                          <CardContent className="p-4">
                            <div className="flex flex-col sm:flex-row gap-4">
                              {/* Prize info */}
                              <div className="flex-1">
                                <div className="flex items-start gap-3 mb-3">
                                  {prize.organizer_logo ? (
                                    <img 
                                      src={prize.organizer_logo} 
                                      alt={prize.organizer_name}
                                      className="w-12 h-12 rounded-lg object-contain bg-white p-1 border"
                                    />
                                  ) : (
                                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
                                      <Gift className="w-6 h-6 text-white" />
                                    </div>
                                  )}
                                  <div>
                                    <h4 className="font-bold text-lg">{prize.prize_name}</h4>
                                    <p className="text-sm text-muted-foreground">
                                      Tirage : {prize.draw_title}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                      Gagné le {formatDate(prize.won_at)}
                                    </p>
                                  </div>
                                </div>
                                
                                {/* Description du lot */}
                                {prize.prize?.description && (
                                  <p className="text-sm text-slate-600 mb-3 bg-white rounded-lg p-3">
                                    {prize.prize.description}
                                  </p>
                                )}
                                
                                {/* Consignes de retrait */}
                                <div className="bg-white rounded-lg p-4 border border-yellow-200 space-y-2">
                                  <h5 className="font-semibold text-sm flex items-center gap-2">
                                    <MapPin className="w-4 h-4 text-yellow-600" />
                                    Consignes de retrait
                                  </h5>
                                  
                                  {prize.withdrawal_instructions ? (
                                    <p className="text-sm text-slate-600">{prize.withdrawal_instructions}</p>
                                  ) : (
                                    <p className="text-sm text-slate-500 italic">
                                      Contactez l'organisateur pour récupérer votre lot.
                                    </p>
                                  )}
                                  
                                  {prize.withdrawal_location && (
                                    <p className="text-sm text-slate-600 flex items-center gap-2">
                                      <MapPin className="w-4 h-4 text-slate-400" />
                                      {prize.withdrawal_location}
                                    </p>
                                  )}
                                  
                                  {prize.withdrawal_deadline && (
                                    <p className="text-sm text-slate-600 flex items-center gap-2">
                                      <Calendar className="w-4 h-4 text-slate-400" />
                                      À retirer avant le {formatDate(prize.withdrawal_deadline)}
                                    </p>
                                  )}
                                  
                                  {prize.organizer_contact && (
                                    <p className="text-sm text-slate-600 flex items-center gap-2">
                                      <Phone className="w-4 h-4 text-slate-400" />
                                      {prize.organizer_contact}
                                    </p>
                                  )}
                                </div>
                                
                                {/* Référence */}
                                {prize.reference && (
                                  <p className="text-xs text-muted-foreground mt-2">
                                    Votre référence : <span className="font-mono font-bold">{prize.reference}</span>
                                  </p>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                  
                  {/* Lots déjà retirés */}
                  {wonPrizes.filter(p => p.is_withdrawn).length > 0 && (
                    <div className="space-y-3 mt-6">
                      <h3 className="font-bold text-lg flex items-center gap-2 text-slate-500">
                        <CheckCircle2 className="w-5 h-5" />
                        Lots retirés ({wonPrizes.filter(p => p.is_withdrawn).length})
                      </h3>
                      {wonPrizes.filter(p => p.is_withdrawn).map(prize => (
                        <Card key={prize.id} className="overflow-hidden opacity-60 bg-slate-50">
                          <CardContent className="p-4">
                            <div className="flex items-center gap-4">
                              {prize.organizer_logo ? (
                                <img 
                                  src={prize.organizer_logo} 
                                  alt={prize.organizer_name}
                                  className="w-10 h-10 rounded-lg object-contain bg-white p-1 border grayscale"
                                />
                              ) : (
                                <div className="w-10 h-10 rounded-lg bg-slate-300 flex items-center justify-center">
                                  <Gift className="w-5 h-5 text-white" />
                                </div>
                              )}
                              <div className="flex-1">
                                <h4 className="font-medium text-slate-500 line-through">{prize.prize_name}</h4>
                                <p className="text-sm text-slate-400">{prize.draw_title}</p>
                              </div>
                              <Badge variant="outline" className="bg-green-100 text-green-700 border-green-300">
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                Retiré
                              </Badge>
                            </div>
                            {prize.withdrawn_at && (
                              <p className="text-xs text-slate-400 mt-2">
                                Retiré le {formatDate(prize.withdrawn_at)}
                              </p>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history">
            <div className="space-y-4">
              <h3 className="font-bold text-lg flex items-center gap-2">
                <Gift className="w-5 h-5 text-amber-500" />
                Tirages ({participations.draws?.length || 0})
              </h3>
              
              {participations.draws?.length === 0 ? (
                <p className="text-muted-foreground py-4">Aucune participation</p>
              ) : (
                <div className="space-y-3">
                  {participations.draws?.map(draw => (
                    <Card key={draw.id}>
                      <CardContent className="p-4 flex items-center justify-between">
                        <div>
                          <h4 className="font-medium">{draw.title}</h4>
                          <p className="text-sm text-muted-foreground">
                            {draw.organizer_name} • {formatDate(draw.created_at)}
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge variant={draw.status === "completed" ? "default" : "secondary"}>
                            {draw.status === "completed" ? "Terminé" : "En cours"}
                          </Badge>
                          <Link to={`/draw/${draw.id}/results`}>
                            <Button variant="ghost" size="sm">
                              <ChevronRight className="w-4 h-4" />
                            </Button>
                          </Link>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Subscriptions Tab */}
          <TabsContent value="subscriptions">
            <div className="space-y-4">
              <h3 className="font-bold text-lg flex items-center gap-2">
                <Bell className="w-5 h-5 text-blue-500" />
                Mes abonnements aux organisateurs
              </h3>
              <p className="text-sm text-muted-foreground">
                Vous recevez des notifications de ces organisateurs lorsqu'ils créent de nouveaux tirages ou jeux.
              </p>
              
              {subscriptions.length === 0 ? (
                <Card className="bg-muted/50">
                  <CardContent className="p-8 text-center">
                    <Bell className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <h4 className="font-medium mb-2">Aucun abonnement</h4>
                    <p className="text-sm text-muted-foreground">
                      Participez à des tirages pour vous abonner automatiquement aux organisateurs.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {subscriptions.map(sub => (
                    <Card key={sub.organizer_id} className={!sub.is_active ? "opacity-50" : ""}>
                      <CardContent className="p-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold">
                            {sub.organizer_name?.charAt(0) || "?"}
                          </div>
                          <div>
                            <h4 className="font-medium">{sub.organizer_name}</h4>
                            <p className="text-sm text-muted-foreground">
                              Abonné depuis {sub.subscribed_at ? formatDate(sub.subscribed_at).split(" ")[0] : "N/A"}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={sub.is_active ? "default" : "secondary"} className={sub.is_active ? "bg-green-100 text-green-700" : ""}>
                            {sub.is_active ? "Actif" : "Inactif"}
                          </Badge>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                            onClick={() => handleUnsubscribe(sub.organizer_id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Footer */}
      <footer className="bg-card border-t py-8 mt-12">
        <div className="max-w-6xl mx-auto px-6">
          <AnimatedFooter />
          <p className="text-center text-muted-foreground text-xs mt-4">
            Pour vous désabonner d'un organisateur, utilisez l'onglet "Abonnements" ci-dessus.
          </p>
        </div>
      </footer>

      {/* PWA Install Banner */}
      <InstallPWAButton variant="banner" />
    </div>
  );
}
